import { AICycleContent } from "../../types/ai-templates";
import { motion } from "framer-motion";
import { useState } from "react";
import NetworkIcon from "@core/network-icon";

interface CycleTemplate4Props {
  content: AICycleContent;
  onUpdate: (newContent: AICycleContent) => void;
}

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.1,
    },
  },
};

export function CycleTemplate4({ content, onUpdate }: CycleTemplate4Props) {
  const [editingStep, setEditingStep] = useState<number | null>(null);
  const [editingField, setEditingField] = useState<
    "title" | "description" | null
  >(null);
  const [tempValue, setTempValue] = useState<string>("");

  // Ensure steps exist
  if (!content.steps || content.steps.length !== 4) {
    return (
      <div className="p-4 text-center text-error">
        Invalid cycle data structure. Exactly 4 steps required.
      </div>
    );
  }

  // Handle step update
  const handleStepUpdate = (
    stepIndex: number,
    field: "title" | "description",
    newText: string,
  ) => {
    const newContent = JSON.parse(JSON.stringify(content));
    if (newContent.steps && newContent.steps[stepIndex]) {
      newContent.steps[stepIndex][field] = newText;
      onUpdate(newContent);
    }
  };

  // Handle title update
  const handleTitleUpdate = (newTitle: string) => {
    const newContent = JSON.parse(JSON.stringify(content));
    newContent.title = newTitle;
    onUpdate(newContent);
  };

  // Handle click to edit
  const startEditing = (
    field: "title" | "description",
    stepIndex: number,
    value: string,
  ) => {
    setEditingField(field);
    setEditingStep(stepIndex);
    setTempValue(value);
  };

  return (
    <motion.div
      className="p-6 bg-base-200 rounded-lg shadow-md overflow-hidden"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <motion.div
        className="relative w-max h-full mx-auto"
        variants={containerVariants}
      >
        <svg
          width="816"
          height="576"
          viewBox="0 0 816 576"
          style={{
            fill: "none",
            stroke: "none",
            fillRule: "evenodd",
            clipRule: "evenodd",
            strokeLinecap: "round",
            strokeLinejoin: "round",
            strokeMiterlimit: 1.5,
          }}
          version="1.1"
          xmlns="http://www.w3.org/2000/svg"
          xmlnsXlink="http://www.w3.org/1999/xlink"
        >
          <style
            className="text-font-style fontImports"
            data-font-family="Roboto"
          >
            @import
            url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;700&display=block');
          </style>

          {/* Patterns */}
          <defs>
            <pattern
              id="crawlingPattern-a869901a"
              x="0"
              y="0"
              width="12"
              height="12"
              patternUnits="userSpaceOnUse"
              fill="#a869901a"
            >
              <use href="#crawlingSymbol"></use>
            </pattern>
            <pattern
              id="crawlingPattern-a867651a"
              x="0"
              y="0"
              width="12"
              height="12"
              patternUnits="userSpaceOnUse"
              fill="#a867651a"
            >
              <use href="#crawlingSymbol"></use>
            </pattern>
            <pattern
              id="crawlingPattern-9b7b5f1a"
              x="0"
              y="0"
              width="12"
              height="12"
              patternUnits="userSpaceOnUse"
              fill="#9b7b5f1a"
            >
              <use href="#crawlingSymbol"></use>
            </pattern>
            <pattern
              id="crawlingPattern-958e541a"
              x="0"
              y="0"
              width="12"
              height="12"
              patternUnits="userSpaceOnUse"
              fill="#958e541a"
            >
              <use href="#crawlingSymbol"></use>
            </pattern>
            <pattern
              id="crawlingPattern-0000001b"
              x="0"
              y="0"
              width="12"
              height="12"
              patternUnits="userSpaceOnUse"
              fill="#0000001b"
            >
              <use href="#crawlingSymbol"></use>
            </pattern>
            <pattern
              id="crawlingPattern-f17400d3"
              x="0"
              y="0"
              width="12"
              height="12"
              patternUnits="userSpaceOnUse"
              fill="#f17400d3"
            >
              <use href="#crawlingSymbol"></use>
            </pattern>
            <pattern
              id="crawlingPattern-ddc700ff"
              x="0"
              y="0"
              width="12"
              height="12"
              patternUnits="userSpaceOnUse"
              fill="#ddc700ff"
            >
              <use href="#crawlingSymbol"></use>
            </pattern>
            <pattern
              id="crawlingPattern-f9080081"
              x="0"
              y="0"
              width="12"
              height="12"
              patternUnits="userSpaceOnUse"
              fill="#f9080081"
            >
              <use href="#crawlingSymbol"></use>
            </pattern>
            <pattern
              id="crawlingPattern-eb008e76"
              x="0"
              y="0"
              width="12"
              height="12"
              patternUnits="userSpaceOnUse"
              fill="#eb008e76"
            >
              <use href="#crawlingSymbol"></use>
            </pattern>
            <pattern
              id="crawlingPattern-5f5f5f1a"
              x="0"
              y="0"
              width="12"
              height="12"
              patternUnits="userSpaceOnUse"
              fill="#5f5f5f1a"
            >
              <use href="#crawlingSymbol"></use>
            </pattern>
            <symbol id="crawlingSymbol">
              <g>
                <animateTransform
                  attributeName="transform"
                  type="translate"
                  from="0 0"
                  to="-12 0"
                  dur="0.4s"
                  repeatCount="indefinite"
                ></animateTransform>
                <path d="M -12.5 12.5 l 13 -13 l 6 0 l -13 13 Z M -0.5 12.5 l 13 -13 l 6 0 l -13 13 Z M 11.5 12.5 l 13 -13 l 6 0 l -13 13 Z"></path>
              </g>
            </symbol>
          </defs>

          {/* Paths */}
          <g
            id="g-root-cp_4_g-4_1q5qneiz0ue4p-stroke-fill"
            data-item-order="-339254"
            transform="translate(231.26199340820312, 122)"
          >
            <g id="cp_4_g-4_1q5qneiz0ue4p-fill" stroke="none" fill="#69425a">
              <path d="M 144.268281 99C 110.909781 108.5921 84.540081 135.2141 74.738281 169.0897C 85.807381 173.3228 93.684381 184.2195 93.684381 197L 171.738281 197L 171.738281 118.7039C 159.088981 118.7039 148.316781 110.4867 144.268281 99Z"></path>
            </g>
            <g id="cp_4_g-4_1q5qneiz0ue4p-fill-overlay" stroke="none" fill="">
              <path d="M 144.268281 99C 110.909781 108.5921 84.540081 135.2141 74.738281 169.0897C 85.807381 173.3228 93.684381 184.2195 93.684381 197L 171.738281 197L 171.738281 118.7039C 159.088981 118.7039 148.316781 110.4867 144.268281 99Z"></path>
            </g>
            <g
              id="cp_4_g-4_1q5qneiz0ue4p-stroke"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeMiterlimit="4"
              stroke={content.steps[0].color || "#f366bb"}
              strokeWidth="2"
            >
              <path d="M 144.268281 99C 110.909781 108.5921 84.540081 135.2141 74.738281 169.0897C 85.807381 173.3228 93.684381 184.2195 93.684381 197L 171.738281 197L 171.738281 118.7039C 159.088981 118.7039 148.316781 110.4867 144.268281 99Z"></path>
            </g>
            <g
              id="cp_4_g-4_1q5qneiz0ue4p-stroke-overlay"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeMiterlimit="4"
              stroke={`${content.steps[0].color}` || "#eb008e5b"}
              strokeWidth="2"
              strokeDasharray="4,6"
            >
              <animate
                attributeName="stroke-dashoffset"
                values="30;0"
                dur="1s"
                repeatCount="indefinite"
                begin="0"
              ></animate>
              <path d="M 144.268281 99C 110.909781 108.5921 84.540081 135.2141 74.738281 169.0897C 85.807381 173.3228 93.684381 184.2195 93.684381 197L 171.738281 197L 171.738281 118.7039C 159.088981 118.7039 148.316781 110.4867 144.268281 99Z"></path>
            </g>
            <g id="cp_4_g-4_1q5qneiz0ue4p-fill" stroke="none" fill="#69425a">
              <path d="M 10 164.089L 32.2374 136L 60.7689 172.04C 72.0703 121.422 117.014 83.6049 170.737 83.6049L 169.737 105L 194.085 86.0409L 230.737 57.5L 201.781 34.952L 169.737 10L 170.737 32C 91.3642 32 25.1609 88.7309 10 164.089Z"></path>
            </g>
            <g id="cp_4_g-4_1q5qneiz0ue4p-fill-overlay" stroke="none" fill="">
              <path d="M 10 164.089L 32.2374 136L 60.7689 172.04C 72.0703 121.422 117.014 83.6049 170.737 83.6049L 169.737 105L 194.085 86.0409L 230.737 57.5L 201.781 34.952L 169.737 10L 170.737 32C 91.3642 32 25.1609 88.7309 10 164.089Z"></path>
            </g>
            <g
              id="cp_4_g-4_1q5qneiz0ue4p-stroke"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeMiterlimit="4"
              stroke={content.steps[0].color || "#f366bb"}
              strokeWidth="2"
            >
              <path d="M 10 164.0894L 32.2374 136L 60.7689 172.0398C 72.0703 121.4216 117.0144 83.6049 170.7374 83.6049L 169.7374 105L 230.7374 57.5L 169.7374 10L 170.7374 32C 91.3642 32 25.1609 88.7309 10 164.0894Z"></path>
            </g>
            <g
              id="cp_4_g-4_1q5qneiz0ue4p-stroke-overlay"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeMiterlimit="4"
              stroke={`${content.steps[0].color}` || "#eb008e5b"}
              strokeWidth="2"
              strokeDasharray="4,6"
            >
              <animate
                attributeName="stroke-dashoffset"
                values="30;0"
                dur="1s"
                repeatCount="indefinite"
                begin="0"
              ></animate>
              <path d="M 10 164.0894L 32.2374 136L 60.7689 172.0398C 72.0703 121.4216 117.0144 83.6049 170.7374 83.6049L 169.7374 105L 230.7374 57.5L 169.7374 10L 170.7374 32C 91.3642 32 25.1609 88.7309 10 164.0894Z"></path>
            </g>
          </g>

          <g
            id="g-root-cp_3_g-3_1lqjmd6z0ubr8-stroke-fill"
            data-item-order="-339248"
            transform="translate(206, 248)"
          >
            <g id="cp_3_g-3_1lqjmd6z0ubr8-fill" stroke="none" fill="#68403f">
              <path d="M 99 98.3901C 110.4681 94.4629 118.7021 83.6869 118.7021 71L 197 71L 197 149.838C 184.3112 149.838 173.5053 157.8292 169.4441 169C 135.1569 159.4453 108.2314 132.4974 99 98.3901Z"></path>
            </g>
            <g id="cp_3_g-3_1lqjmd6z0ubr8-fill-overlay" stroke="none" fill="">
              <path d="M 99 98.3901C 110.4681 94.4629 118.7021 83.6869 118.7021 71L 197 71L 197 149.838C 184.3112 149.838 173.5053 157.8292 169.4441 169C 135.1569 159.4453 108.2314 132.4974 99 98.3901Z"></path>
            </g>
            <g
              id="cp_3_g-3_1lqjmd6z0ubr8-stroke"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeMiterlimit="4"
              stroke={content.steps[2].color || "#fb6762"}
              strokeWidth="2"
            >
              <path d="M 99 98.3901C 110.4681 94.4629 118.7021 83.6869 118.7021 71L 197 71L 197 149.838C 184.3112 149.838 173.5053 157.8292 169.4441 169C 135.1569 159.4453 108.2314 132.4974 99 98.3901Z"></path>
            </g>
            <g
              id="cp_3_g-3_1lqjmd6z0ubr8-stroke-overlay"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeMiterlimit="4"
              stroke={`${content.steps[2].color}` || "#f9080063"}
              strokeWidth="2"
              strokeDasharray="4,6"
            >
              <animate
                attributeName="stroke-dashoffset"
                values="30;0"
                dur="1s"
                repeatCount="indefinite"
                begin="0"
              ></animate>
              <path d="M 99 98.3901C 110.4681 94.4629 118.7021 83.6869 118.7021 71L 197 71L 197 149.838C 184.3112 149.838 173.5053 157.8292 169.4441 169C 135.1569 159.4453 108.2314 132.4974 99 98.3901Z"></path>
            </g>
            <g id="cp_3_g-3_1lqjmd6z0ubr8-fill" stroke="none" fill="#68403f">
              <path d="M 35.2626 38.0894L 57.5 10L 86.0315 46.0398L 105 70L 83.2965 70C 83.2936 70.333 83.2922 70.6663 83.2922 71C 83.2922 124.859 120.613 169.948 170.656 181.517L 137 208L 169.953 233.931C 91.7571 221.376 32 153.209 32 71C 32 70.6664 32.001 70.3331 32.0029 70L 10 70L 35.2626 38.0894Z"></path>
            </g>
            <g id="cp_3_g-3_1lqjmd6z0ubr8-fill-overlay" stroke="none" fill="">
              <path d="M 35.2626 38.0894L 57.5 10L 86.0315 46.0398L 105 70L 83.2965 70C 83.2936 70.333 83.2922 70.6663 83.2922 71C 83.2922 124.859 120.613 169.948 170.656 181.517L 137 208L 169.953 233.931C 91.7571 221.376 32 153.209 32 71C 32 70.6664 32.001 70.3331 32.0029 70L 10 70L 35.2626 38.0894Z"></path>
            </g>
            <g
              id="cp_3_g-3_1lqjmd6z0ubr8-stroke"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeMiterlimit="4"
              stroke={content.steps[2].color || "#fb6762"}
              strokeWidth="2"
            >
              <path d="M 57.5 10L 105 70L 83.2965 70C 83.2936 70.333 83.2922 70.6663 83.2922 71C 83.2922 124.8588 120.6133 169.9476 170.6558 181.5167L 137 208L 169.9534 233.9305C 91.7571 221.3757 32 153.2089 32 71C 32 70.6664 32.001 70.3331 32.0029 70L 10 70L 57.5 10Z"></path>
            </g>
            <g
              id="cp_3_g-3_1lqjmd6z0ubr8-stroke-overlay"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeMiterlimit="4"
              stroke={`${content.steps[2].color}` || "#f9080063"}
              strokeWidth="2"
              strokeDasharray="4,6"
            >
              <animate
                attributeName="stroke-dashoffset"
                values="30;0"
                dur="1s"
                repeatCount="indefinite"
                begin="0"
              ></animate>
              <path d="M 57.5 10L 105 70L 83.2965 70C 83.2936 70.333 83.2922 70.6663 83.2922 71C 83.2922 124.8588 120.6133 169.9476 170.6558 181.5167L 137 208L 169.9534 233.9305C 91.7571 221.3757 32 153.2089 32 71C 32 70.6664 32.001 70.3331 32.0029 70L 10 70L 57.5 10Z"></path>
            </g>
          </g>

          <g
            id="g-root-cp_2_g-2_1lqjmd6z0ubrb-stroke-fill"
            data-item-order="-339242"
            transform="translate(333, 309)"
          >
            <g id="cp_2_g-2_1lqjmd6z0ubrb-fill" stroke="none" fill="#5c4938">
              <path d="M 70 88.838C 82.4299 88.838 93.0152 96.8292 96.9935 108C 130.581 98.4453 156.957 71.4974 166 37.3901C 154.766 33.4629 146.7 22.6869 146.7 10L 70 10L 70 88.838Z"></path>
            </g>
            <g id="cp_2_g-2_1lqjmd6z0ubrb-fill-overlay" stroke="none" fill="">
              <path d="M 70 88.838C 82.4299 88.838 93.0152 96.8292 96.9935 108C 130.581 98.4453 156.957 71.4974 166 37.3901C 154.766 33.4629 146.7 22.6869 146.7 10L 70 10L 70 88.838Z"></path>
            </g>
            <g
              id="cp_2_g-2_1lqjmd6z0ubrb-stroke"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeMiterlimit="4"
              stroke={content.steps[3].color || "#f79438"}
              strokeWidth="2"
            >
              <path d="M 70 88.838C 82.4299 88.838 93.0152 96.8292 96.9935 108C 130.581 98.4453 156.957 71.4974 166 37.3901C 154.766 33.4629 146.7 22.6869 146.7 10L 70 10L 70 88.838Z"></path>
            </g>
            <g
              id="cp_2_g-2_1lqjmd6z0ubrb-stroke-overlay"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeMiterlimit="4"
              stroke={`${content.steps[3].color}` || "#f57600ab"}
              strokeWidth="2"
              strokeDasharray="4,6"
            >
              <animate
                attributeName="stroke-dashoffset"
                values="30;0"
                dur="1s"
                repeatCount="indefinite"
                begin="0"
              ></animate>
              <path d="M 70 88.838C 82.4299 88.838 93.0152 96.8292 96.9935 108C 130.581 98.4453 156.957 71.4974 166 37.3901C 154.766 33.4629 146.7 22.6869 146.7 10L 70 10L 70 88.838Z"></path>
            </g>
            <g id="cp_2_g-2_1lqjmd6z0ubrb-fill" stroke="none" fill="#5c4938">
              <path d="M 207.5 67.001719L 179.718 31.324219C 169.923 83.120719 125.11 122.432019 71 123.379319L 71 99.001719L 43.6558 120.518419L 10 147.001219L 42.9534 172.932219L 71 195.001219L 71 174.989219C 151.514 174.021219 218.07 114.680719 230.803 37.075919L 207.5 67.001719Z"></path>
            </g>
            <g id="cp_2_g-2_1lqjmd6z0ubrb-fill-overlay" stroke="none" fill="">
              <path d="M 207.5 67.001719L 179.718 31.324219C 169.923 83.120719 125.11 122.432019 71 123.379319L 71 99.001719L 43.6558 120.518419L 10 147.001219L 42.9534 172.932219L 71 195.001219L 71 174.989219C 151.514 174.021219 218.07 114.680719 230.803 37.075919L 207.5 67.001719Z"></path>
            </g>
            <g
              id="cp_2_g-2_1lqjmd6z0ubrb-stroke"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeMiterlimit="4"
              stroke={content.steps[3].color || "#f79438"}
              strokeWidth="2"
            >
              <path d="M 207.5 67.001719L 179.7184 31.324219C 169.9232 83.120719 125.1097 122.432019 71 123.379319L 71 99.001719L 10 147.001719L 71 195.001719L 71 174.989719C 151.5139 174.021619 218.0702 114.680719 230.8029 37.075919L 207.5 67.001719Z"></path>
            </g>
            <g
              id="cp_2_g-2_1lqjmd6z0ubrb-stroke-overlay"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeMiterlimit="4"
              stroke={`${content.steps[3].color}` || "#f57600ab"}
              strokeWidth="2"
              strokeDasharray="4,6"
            >
              <animate
                attributeName="stroke-dashoffset"
                values="30;0"
                dur="1s"
                repeatCount="indefinite"
                begin="0"
              ></animate>
              <path d="M 207.5 67.001719L 179.7184 31.324219C 169.9232 83.120719 125.1097 122.432019 71 123.379319L 71 99.001719L 10 147.001719L 71 195.001719L 71 174.989719C 151.5139 174.021619 218.0702 114.680719 230.8029 37.075919L 207.5 67.001719Z"></path>
            </g>
          </g>

          <g
            id="g-root-cp_1_g-1_1haq48qz0ucjm-stroke-fill"
            data-item-order="-339236"
            transform="translate(393, 146.9530029296875)"
          >
            <g id="cp_1_g-1_1haq48qz0ucjm-fill" stroke="none" fill="#54502f">
              <path d="M 32.347656 61.089L 69.000256 32.548L 40.043856 10C 114.515956 24.3552 171.092656 89.3865 172.952656 168.048L 195.000656 168.048L 170.802656 199.122L 147.500656 229.048L 119.718556 193.371L 100.000256 168.048L 121.639256 168.048C 119.820256 115.26 82.138656 71.6411 32.347656 61.089Z"></path>
            </g>
            <g id="cp_1_g-1_1haq48qz0ucjm-fill-overlay" stroke="none" fill="">
              <path d="M 32.347656 61.089L 69.000256 32.548L 40.043856 10C 114.515956 24.3552 171.092656 89.3865 172.952656 168.048L 195.000656 168.048L 170.802656 199.122L 147.500656 229.048L 119.718556 193.371L 100.000256 168.048L 121.639256 168.048C 119.820256 115.26 82.138656 71.6411 32.347656 61.089Z"></path>
            </g>
            <g
              id="cp_1_g-1_1haq48qz0ucjm-stroke"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeMiterlimit="4"
              stroke={content.steps[1].color || "#ffe711"}
              strokeWidth="2"
            >
              <path d="M 32.347656 61.089L 69.000256 32.548L 40.043856 10C 114.515956 24.3552 171.092856 89.3865 172.952956 168.048L 195.000256 168.048L 147.500256 229.048L 100.000256 168.048L 121.639256 168.048C 119.820256 115.2602 82.138656 71.6411 32.347656 61.089Z"></path>
            </g>
            <g
              id="cp_1_g-1_1haq48qz0ucjm-stroke-overlay"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeMiterlimit="4"
              stroke={`${content.steps[1].color}` || "#e9d100ff"}
              strokeWidth="2"
              strokeDasharray="4,6"
            >
              <animate
                attributeName="stroke-dashoffset"
                values="30;0"
                dur="1s"
                repeatCount="indefinite"
                begin="0"
              ></animate>
              <path d="M 32.347656 61.089L 69.000256 32.548L 40.043856 10C 114.515956 24.3552 171.092856 89.3865 172.952956 168.048L 195.000256 168.048L 147.500256 229.048L 100.000256 168.048L 121.639256 168.048C 119.820256 115.2602 82.138656 71.6411 32.347656 61.089Z"></path>
            </g>
            <g id="cp_1_g-1_1haq48qz0ucjm-fill" stroke="none" fill="#54502f">
              <path d="M 105 144.136575C 94.1591 148.369675 86.4445 159.266375 86.4445 172.046875L 10 172.046875L 10 93.750775C 22.3885 93.750775 32.9386 85.533575 36.9036 74.046875C 69.5743 83.638975 95.4003 110.260975 105 144.136575Z"></path>
            </g>
            <g id="cp_1_g-1_1haq48qz0ucjm-fill-overlay" stroke="none" fill="">
              <path d="M 105 144.136575C 94.1591 148.369675 86.4445 159.266375 86.4445 172.046875L 10 172.046875L 10 93.750775C 22.3885 93.750775 32.9386 85.533575 36.9036 74.046875C 69.5743 83.638975 95.4003 110.260975 105 144.136575Z"></path>
            </g>
            <g
              id="cp_1_g-1_1haq48qz0ucjm-stroke"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeMiterlimit="4"
              stroke={content.steps[1].color || "#ffe711"}
              strokeWidth="2"
            >
              <path d="M 105 144.136575C 94.1591 148.369675 86.4445 159.266375 86.4445 172.046875L 10 172.046875L 10 93.750775C 22.3885 93.750775 32.9386 85.533575 36.9036 74.046875C 69.5743 83.638975 95.4003 110.260975 105 144.136575Z"></path>
            </g>
            <g
              id="cp_1_g-1_1haq48qz0ucjm-stroke-overlay"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeMiterlimit="4"
              stroke={`${content.steps[1].color}` || "#e9d100ff"}
              strokeWidth="2"
              strokeDasharray="4,6"
            >
              <animate
                attributeName="stroke-dashoffset"
                values="30;0"
                dur="1s"
                repeatCount="indefinite"
                begin="0"
              ></animate>
              <path d="M 105 144.136575C 94.1591 148.369675 86.4445 159.266375 86.4445 172.046875L 10 172.046875L 10 93.750775C 22.3885 93.750775 32.9386 85.533575 36.9036 74.046875C 69.5743 83.638975 95.4003 110.260975 105 144.136575Z"></path>
            </g>
          </g>

          {/* Main Title */}
          <g
            id="g-root-tx_cycleofh_18f33zuz0udcd-stroke-fill"
            data-item-order="0"
            transform="translate(188, 50)"
          >
            <g id="tx_cycleofh_18f33zuz0udcd-fill" stroke="none" fill="#171717">
              <foreignObject x="17.63" y="20" width="300" height="30">
                <div
                  className="w-full text-center"
                  style={{
                    font: "bold 25px Roboto, sans-serif",
                    color: "#171717",
                    whiteSpace: "pre-wrap",
                    cursor: "pointer",
                    display:
                      editingField === "title" && editingStep === -1
                        ? "none"
                        : "block",
                  }}
                  onClick={() => startEditing("title", -1, content.title)}
                >
                  {content.title}
                </div>
                {editingField === "title" && editingStep === -1 && (
                  <input
                    autoFocus
                    value={tempValue}
                    onChange={(e) => setTempValue(e.target.value)}
                    onBlur={() => {
                      setEditingField(null);
                      setEditingStep(null);
                      if (tempValue !== content.title) {
                        handleTitleUpdate(tempValue);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") e.currentTarget.blur();
                    }}
                    className="px-1 w-full rounded bg-base-100 outline-none ring-2 ring-primary"
                    style={{ font: "bold 25px Roboto, sans-serif" }}
                  />
                )}
              </foreignObject>
            </g>
            <g id="tx_cycleofh_18f33zuz0udcd-fill-overlay" stroke="none">
              <text
                style={{
                  font: "bold 25px Roboto, sans-serif",
                  whiteSpace: "pre",
                }}
                fontWeight="bold"
                fontSize="25px"
                fontFamily="Roboto, sans-serif"
              >
                <tspan x="17.63" y="42.5" dominant-baseline="ideographic">
                  {content.title}
                </tspan>
              </text>
            </g>
          </g>

          {/* Step 1 */}
          <g
            id="g-root-tx_dataproc_8xs16iz0ubr8-stroke-fill"
            data-item-order="0"
            transform="translate(20, 110)"
          >
            <g
              id="tx_dataproc_8xs16iz0ubr8-fill"
              stroke="none"
              fill={content.steps[0].color || "#f366bb"}
            >
              <foreignObject x="12" y="10" width="200" height="80">
                <div
                  style={{
                    font: "bold 20px Roboto, sans-serif",
                    color: content.steps[0].color || "#f366bb",
                    whiteSpace: "pre-wrap",
                    cursor: "pointer",
                    display:
                      editingField === "title" && editingStep === 0
                        ? "none"
                        : "block",
                  }}
                  onClick={() =>
                    startEditing("title", 0, content.steps[0].title)
                  }
                >
                  {content.steps[0].title}
                </div>
                {editingField === "title" && editingStep === 0 && (
                  <textarea
                    autoFocus
                    value={tempValue}
                    onChange={(e) => setTempValue(e.target.value)}
                    onBlur={() => {
                      setEditingField(null);
                      setEditingStep(null);
                      if (tempValue !== content.steps[0].title) {
                        handleStepUpdate(0, "title", tempValue);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        e.currentTarget.blur();
                      }
                    }}
                    className="px-1 w-full rounded bg-base-100 outline-none ring-2 ring-primary"
                    style={{ font: "bold 20px Roboto, sans-serif" }}
                  />
                )}
              </foreignObject>
            </g>
            <g id="tx_dataproc_8xs16iz0ubr8-fill-overlay" stroke="none">
              <text
                style={{
                  font: "bold 20px Roboto, sans-serif",
                  whiteSpace: "pre",
                }}
                fontWeight="bold"
                fontSize="20px"
                fontFamily="Roboto, sans-serif"
              >
                {content.steps[0].title.split("\n").map((line, i) => (
                  <tspan
                    key={i}
                    x="12"
                    y={33.5 + i * 24}
                    dominant-baseline="ideographic"
                  >
                    {line}
                  </tspan>
                ))}
              </text>
            </g>
          </g>
          <g
            id="g-root-tx_enhancem_250xmz0ucjx-stroke-fill"
            data-item-order="0"
            transform="translate(20, 140)"
          >
            <g id="tx_enhancem_250xmz0ucjx-fill" stroke="none" fill="#171717">
              <foreignObject x="12" y="10" width="200" height="60">
                <div
                  style={{
                    font: "15px Roboto, sans-serif",
                    color: "#171717",
                    whiteSpace: "pre-wrap",
                    overflowWrap: "break-word",
                    maxWidth: "150px",
                    cursor: "pointer",
                    display:
                      editingField === "description" && editingStep === 0
                        ? "none"
                        : "block",
                  }}
                  onClick={() =>
                    startEditing("description", 0, content.steps[0].description)
                  }
                >
                  {content.steps[0].description}
                </div>
                {editingField === "description" && editingStep === 0 && (
                  <textarea
                    autoFocus
                    value={tempValue}
                    onChange={(e) => setTempValue(e.target.value)}
                    onBlur={() => {
                      setEditingField(null);
                      setEditingStep(null);
                      if (tempValue !== content.steps[0].description) {
                        handleStepUpdate(0, "description", tempValue);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        e.currentTarget.blur();
                      }
                    }}
                    className="px-1 w-full rounded bg-base-100 outline-none ring-2 ring-primary"
                    style={{
                      font: "15px Roboto, sans-serif",
                      maxWidth: "150px",
                    }}
                  />
                )}
              </foreignObject>
            </g>
          </g>

          {/* Step 2 */}
          <g
            id="g-root-tx_problemd_qofkl6z0ucjs-stroke-fill"
            data-item-order="0"
            transform="translate(590, 110)"
          >
            <g
              id="tx_problemd_qofkl6z0ucjs-fill"
              stroke="none"
              fill={content.steps[1].color || "#ffe711"}
            >
              <foreignObject x="16.47" y="10" width="200" height="60">
                <div
                  style={{
                    font: "bold 20px Roboto, sans-serif",
                    color: content.steps[1].color || "#ffe711",
                    whiteSpace: "pre-wrap",
                    cursor: "pointer",
                    display:
                      editingField === "title" && editingStep === 1
                        ? "none"
                        : "block",
                  }}
                  onClick={() =>
                    startEditing("title", 1, content.steps[1].title)
                  }
                >
                  {content.steps[1].title}
                </div>
                {editingField === "title" && editingStep === 1 && (
                  <textarea
                    autoFocus
                    value={tempValue}
                    onChange={(e) => setTempValue(e.target.value)}
                    onBlur={() => {
                      setEditingField(null);
                      setEditingStep(null);
                      if (tempValue !== content.steps[1].title) {
                        handleStepUpdate(1, "title", tempValue);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        e.currentTarget.blur();
                      }
                    }}
                    className="px-1 w-full rounded bg-base-100 outline-none ring-2 ring-primary"
                    style={{ font: "bold 20px Roboto, sans-serif" }}
                  />
                )}
              </foreignObject>
            </g>
            <g id="tx_problemd_qofkl6z0ucjs-fill-overlay" stroke="none">
              <text
                style={{
                  font: "bold 20px Roboto, sans-serif",
                  whiteSpace: "pre",
                }}
                fontWeight="bold"
                fontSize="20px"
                fontFamily="Roboto, sans-serif"
              >
                {content.steps[1].title.split("\n").map((line, i) => (
                  <tspan
                    key={i}
                    x={i === 0 ? "76.12" : "16.47"}
                    y={33.5 + i * 24}
                    dominant-baseline="ideographic"
                  >
                    {line}
                  </tspan>
                ))}
              </text>
            </g>
          </g>
          <g
            id="g-root-tx_breaking_m8m2gqz0udc1-stroke-fill"
            data-item-order="0"
            transform="translate(602, 140)"
          >
            <g id="tx_breaking_m8m2gqz0udc1-fill" stroke="none" fill="#171717">
              <foreignObject x="6" y="10" width="200" height="60">
                <div
                  style={{
                    font: "15px Roboto, sans-serif",
                    color: "#171717",
                    whiteSpace: "pre-wrap",
                    overflowWrap: "break-word",
                    maxWidth: "150px",
                    cursor: "pointer",
                    display:
                      editingField === "description" && editingStep === 1
                        ? "none"
                        : "block",
                  }}
                  onClick={() =>
                    startEditing("description", 1, content.steps[1].description)
                  }
                >
                  {content.steps[1].description}
                </div>
                {editingField === "description" && editingStep === 1 && (
                  <textarea
                    autoFocus
                    value={tempValue}
                    onChange={(e) => setTempValue(e.target.value)}
                    onBlur={() => {
                      setEditingField(null);
                      setEditingStep(null);
                      if (tempValue !== content.steps[1].description) {
                        handleStepUpdate(1, "description", tempValue);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        e.currentTarget.blur();
                      }
                    }}
                    className="px-1 w-full rounded bg-base-100 outline-none ring-2 ring-primary"
                    style={{
                      font: "15px Roboto, sans-serif",
                      maxWidth: "150px",
                    }}
                  />
                )}
              </foreignObject>
            </g>
          </g>

          {/* Step 3 */}
          <g
            id="g-root-tx_neurosci_dcz27uz0ue4q-stroke-fill"
            data-item-order="0"
            transform="translate(20, 362)"
          >
            <g
              id="tx_neurosci_dcz27uz0ue4q-fill"
              stroke="none"
              fill={content.steps[2].color || "#fb6762"}
            >
              <foreignObject x="12" y="10" width="200" height="60">
                <div
                  style={{
                    font: "bold 20px Roboto, sans-serif",
                    color: content.steps[2].color || "#fb6762",
                    whiteSpace: "pre-wrap",
                    cursor: "pointer",
                    display:
                      editingField === "title" && editingStep === 2
                        ? "none"
                        : "block",
                  }}
                  onClick={() =>
                    startEditing("title", 2, content.steps[2].title)
                  }
                >
                  {content.steps[2].title}
                </div>
                {editingField === "title" && editingStep === 2 && (
                  <textarea
                    autoFocus
                    value={tempValue}
                    onChange={(e) => setTempValue(e.target.value)}
                    onBlur={() => {
                      setEditingField(null);
                      setEditingStep(null);
                      if (tempValue !== content.steps[2].title) {
                        handleStepUpdate(2, "title", tempValue);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        e.currentTarget.blur();
                      }
                    }}
                    className="px-1 w-full rounded bg-base-100 outline-none ring-2 ring-primary"
                    style={{ font: "bold 20px Roboto, sans-serif" }}
                  />
                )}
              </foreignObject>
            </g>
            <g id="tx_neurosci_dcz27uz0ue4q-fill-overlay" stroke="none">
              <text
                style={{
                  font: "bold 20px Roboto, sans-serif",
                  whiteSpace: "pre",
                }}
                fontWeight="bold"
                fontSize="20px"
                fontFamily="Roboto, sans-serif"
              >
                {content.steps[2].title.split("\n").map((line, i) => (
                  <tspan
                    key={i}
                    x="12"
                    y={33.5 + i * 24}
                    dominant-baseline="ideographic"
                  >
                    {line}
                  </tspan>
                ))}
              </text>
            </g>
          </g>
          <g
            id="g-root-tx_progress_8xs16iz0ubr7-stroke-fill"
            data-item-order="0"
            transform="translate(20, 390)"
          >
            <g id="tx_progress_8xs16iz0ubr7-fill" stroke="none" fill="#171717">
              <foreignObject x="12" y="10" width="200" height="60">
                <div
                  style={{
                    font: "15px Roboto, sans-serif",
                    color: "#171717",
                    whiteSpace: "pre-wrap",
                    overflowWrap: "break-word",
                    maxWidth: "150px",
                    cursor: "pointer",
                    display:
                      editingField === "description" && editingStep === 2
                        ? "none"
                        : "block",
                  }}
                  onClick={() =>
                    startEditing("description", 2, content.steps[2].description)
                  }
                >
                  {content.steps[2].description}
                </div>
                {editingField === "description" && editingStep === 2 && (
                  <textarea
                    autoFocus
                    value={tempValue}
                    onChange={(e) => setTempValue(e.target.value)}
                    onBlur={() => {
                      setEditingField(null);
                      setEditingStep(null);
                      if (tempValue !== content.steps[2].description) {
                        handleStepUpdate(2, "description", tempValue);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        e.currentTarget.blur();
                      }
                    }}
                    className="px-1 w-full rounded bg-base-100 outline-none ring-2 ring-primary"
                    style={{
                      font: "15px Roboto, sans-serif",
                      maxWidth: "150px",
                    }}
                  />
                )}
              </foreignObject>
            </g>
          </g>

          {/* Step 4 */}
          <g
            id="g-root-tx_innovati_13z9lvez0ue4m-stroke-fill"
            data-item-order="0"
            transform="translate(614, 362)"
          >
            <g
              id="tx_innovati_13z9lvez0ue4m-fill"
              stroke="none"
              fill={content.steps[3].color || "#f79438"}
            >
              <foreignObject x="11.85" y="10" width="200" height="60">
                <div
                  style={{
                    font: "bold 20px Roboto, sans-serif",
                    color: content.steps[3].color || "#f79438",
                    whiteSpace: "pre-wrap",
                    cursor: "pointer",
                    display:
                      editingField === "title" && editingStep === 3
                        ? "none"
                        : "block",
                  }}
                  onClick={() =>
                    startEditing("title", 3, content.steps[3].title)
                  }
                >
                  {content.steps[3].title}
                </div>
                {editingField === "title" && editingStep === 3 && (
                  <textarea
                    autoFocus
                    value={tempValue}
                    onChange={(e) => setTempValue(e.target.value)}
                    onBlur={() => {
                      setEditingField(null);
                      setEditingStep(null);
                      if (tempValue !== content.steps[3].title) {
                        handleStepUpdate(3, "title", tempValue);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        e.currentTarget.blur();
                      }
                    }}
                    className="px-1 w-full rounded bg-base-100 outline-none ring-2 ring-primary"
                    style={{ font: "bold 20px Roboto, sans-serif" }}
                  />
                )}
              </foreignObject>
            </g>
            <g id="tx_innovati_13z9lvez0ue4m-fill-overlay" stroke="none">
              <text
                style={{
                  font: "bold 20px Roboto, sans-serif",
                  whiteSpace: "pre",
                }}
                fontWeight="bold"
                fontSize="20px"
                fontFamily="Roboto, sans-serif"
              >
                {content.steps[3].title.split("\n").map((line, i) => (
                  <tspan
                    key={i}
                    x={i === 0 ? "11.85" : "48.59"}
                    y={33.5 + i * 24}
                    dominant-baseline="ideographic"
                  >
                    {line}
                  </tspan>
                ))}
              </text>
            </g>
          </g>
          <g
            id="g-root-tx_advancem_qofkl6z0ucjr-stroke-fill"
            data-item-order="0"
            transform="translate(614, 390)"
          >
            <g id="tx_advancem_qofkl6z0ucjr-fill" stroke="none" fill="#171717">
              <foreignObject x="12.66" y="10" width="200" height="60">
                <div
                  style={{
                    font: "15px Roboto, sans-serif",
                    color: "#171717",
                    whiteSpace: "pre-wrap",
                    overflowWrap: "break-word",
                    maxWidth: "150px",
                    cursor: "pointer",
                    display:
                      editingField === "description" && editingStep === 3
                        ? "none"
                        : "block",
                  }}
                  onClick={() =>
                    startEditing("description", 3, content.steps[3].description)
                  }
                >
                  {content.steps[3].description}
                </div>
                {editingField === "description" && editingStep === 3 && (
                  <textarea
                    autoFocus
                    value={tempValue}
                    onChange={(e) => setTempValue(e.target.value)}
                    onBlur={() => {
                      setEditingField(null);
                      setEditingStep(null);
                      if (tempValue !== content.steps[3].description) {
                        handleStepUpdate(3, "description", tempValue);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        e.currentTarget.blur();
                      }
                    }}
                    className="px-1 w-full rounded bg-base-100 outline-none ring-2 ring-primary"
                    style={{
                      font: "15px Roboto, sans-serif",
                      maxWidth: "150px",
                    }}
                  />
                )}
              </foreignObject>
            </g>
          </g>

          {/* Icons */}
          <g
            id="g-root-ic_code_250xmz0ucjy-stroke-fill"
            data-item-order="0"
            transform="translate(330, 246)"
          >
            <foreignObject x="0" y="0" width="60" height="60">
              <NetworkIcon
                value={content.steps[0].iconId || "droplets"}
                type="LUCIDE"
                style={{
                  color: content.steps[0].color || "#f366bb",
                  width: "60px",
                  height: "60px",
                }}
              />
            </foreignObject>
          </g>
          <g
            id="g-root-ic_proc_2ri0qz0zzex-stroke-fill"
            data-item-order="0"
            transform="translate(408, 246)"
          >
            <foreignObject x="0" y="0" width="60" height="60">
              <NetworkIcon
                value={content.steps[1].iconId || "cloud-rain-wind"}
                type="LUCIDE"
                style={{
                  color: content.steps[1].color || "#ffe711",
                  width: "60px",
                  height: "60px",
                }}
              />
            </foreignObject>
          </g>
          <g
            id="g-root-ic_brai_1uqjy7uz0vqxb-stroke-fill"
            data-item-order="0"
            transform="translate(330, 324)"
          >
            <foreignObject x="0" y="0" width="60" height="60">
              <NetworkIcon
                value={content.steps[2].iconId || "cloud-rain"}
                type="LUCIDE"
                style={{
                  color: content.steps[2].color || "#fb6762",
                  width: "60px",
                  height: "60px",
                }}
              />
            </foreignObject>
          </g>
          <g
            id="g-root-ic_2_8drsqz0x4w8-stroke-fill"
            data-item-order="0"
            transform="translate(408, 324)"
          >
            <foreignObject x="0" y="0" width="60" height="60">
              <NetworkIcon
                value={content.steps[3].iconId || "fish"}
                type="LUCIDE"
                style={{
                  color: content.steps[3].color || "#f79438",
                  width: "60px",
                  height: "60px",
                }}
              />
            </foreignObject>
          </g>

          {/* Number Labels */}
          <g
            id="g-root-tx_4_m7d7gaz0gave-stroke-fill"
            data-item-order="0"
            transform="translate(380, 151)"
          >
            <g
              id="tx_4_m7d7gaz0gave-fill"
              stroke="none"
              fill={content.steps[0].color || "#f366bb"}
            >
              <text
                style={{
                  font: "bold 30px Roboto, sans-serif",
                  whiteSpace: "pre",
                }}
                fontWeight="bold"
                fontSize="30px"
                fontFamily="Roboto, sans-serif"
              >
                <tspan x="13.39" y="45.5" dominant-baseline="ideographic">
                  4
                </tspan>
              </text>
            </g>
            <g id="tx_4_m7d7gaz0gave-fill-overlay" stroke="none">
              <text
                style={{
                  font: "bold 30px Roboto, sans-serif",
                  whiteSpace: "pre",
                }}
                fontWeight="bold"
                fontSize="30px"
                fontFamily="Roboto, sans-serif"
              >
                <tspan x="13.39" y="45.5" dominant-baseline="ideographic">
                  4
                </tspan>
              </text>
            </g>
          </g>
          <g
            id="g-root-tx_3_8vwp2yz0gcgb-stroke-fill"
            data-item-order="0"
            transform="translate(242, 290)"
          >
            <g
              id="tx_3_8vwp2yz0gcgb-fill"
              stroke="none"
              fill={content.steps[2].color || "#fb6762"}
            >
              <text
                style={{
                  font: "bold 30px Roboto, sans-serif",
                  whiteSpace: "pre",
                }}
                fontWeight="bold"
                fontSize="30px"
                fontFamily="Roboto, sans-serif"
              >
                <tspan x="13.39" y="45.5" dominant-baseline="ideographic">
                  3
                </tspan>
              </text>
            </g>
            <g id="tx_3_8vwp2yz0gcgb-fill-overlay" stroke="none">
              <text
                style={{
                  font: "bold 30px Roboto, sans-serif",
                  whiteSpace: "pre",
                }}
                fontWeight="bold"
                fontSize="30px"
                fontFamily="Roboto, sans-serif"
              >
                <tspan x="13.39" y="45.5" dominant-baseline="ideographic">
                  3
                </tspan>
              </text>
            </g>
          </g>
          <g
            id="g-root-tx_2_4gpo1mz0ga2s-stroke-fill"
            data-item-order="0"
            transform="translate(380, 431)"
          >
            <g
              id="tx_2_4gpo1mz0ga2s-fill"
              stroke="none"
              fill={content.steps[3].color || "#f79438"}
            >
              <text
                style={{
                  font: "bold 30px Roboto, sans-serif",
                  whiteSpace: "pre",
                }}
                fontWeight="bold"
                fontSize="30px"
                fontFamily="Roboto, sans-serif"
              >
                <tspan x="13.39" y="45.5" dominant-baseline="ideographic">
                  2
                </tspan>
              </text>
            </g>
            <g id="tx_2_4gpo1mz0ga2s-fill-overlay" stroke="none">
              <text
                style={{
                  font: "bold 30px Roboto, sans-serif",
                  whiteSpace: "pre",
                }}
                fontWeight="bold"
                fontSize="30px"
                fontFamily="Roboto, sans-serif"
              >
                <tspan x="13.39" y="45.5" dominant-baseline="ideographic">
                  2
                </tspan>
              </text>
            </g>
          </g>
          <g
            id="g-root-tx_1_4gpo1mz0ga2t-stroke-fill"
            data-item-order="0"
            transform="translate(517, 290)"
          >
            <g
              id="tx_1_4gpo1mz0ga2t-fill"
              stroke="none"
              fill={content.steps[1].color || "#ffe711"}
            >
              <text
                style={{
                  font: "bold 30px Roboto, sans-serif",
                  whiteSpace: "pre",
                }}
                fontWeight="bold"
                fontSize="30px"
                fontFamily="Roboto, sans-serif"
              >
                <tspan x="13.39" y="45.5" dominant-baseline="ideographic">
                  1
                </tspan>
              </text>
            </g>
            <g
              id="tx_1_4gpo1mz0ga2t-fill-overlay"
              stroke="none"
              fill="url(#crawlingPattern-ddc700ff)"
            >
              <text
                style={{
                  font: "bold 30px Roboto, sans-serif",
                  whiteSpace: "pre",
                }}
                fontWeight="bold"
                fontSize="30px"
                fontFamily="Roboto, sans-serif"
              >
                <tspan x="13.39" y="45.5" dominant-baseline="ideographic">
                  1
                </tspan>
              </text>
            </g>
          </g>
        </svg>
      </motion.div>
    </motion.div>
  );
}
